import React, { useEffect, useRef, useState } from "react";
import Modal from "../../components/Modal";
import { errorMessage } from "../../services";
import { resetOwnerPassword } from "../../services/owners";
import { Spinner } from "../../components/Loader";

// Super Admin, All websites: a WhatsApp message to a website's customer, ready to edit, then opened in WhatsApp with
// the text filled in, or copied. kind "welcome": the store and admin panel links and login; "custom": your own text.
// Passwords are never readable, so the welcome message carries a new temporary password: made as it opens while the
// customer still has their first (temporary) one, or on request once they have set their own (it replaces theirs).
// The Admin ID is the customer's email (they can sign in with it), else their username or phone.

const ADMIN_URL = window.location.origin;

// 10 digits = an Indian number; a longer number already has its country code
export const whatsappNumber = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
};

const greeting = (site) => `Hi ${site.owner || site.name} 👋\nThis is Team Ownezzy, for your Ownezzy website *${site.name}*.`;

function welcomeText(site, password) {
  return [
    greeting(site),
    "",
    "Your website is ready! Here are your details:",
    `🌐 Store: ${site.url}`,
    `🛠️ Admin panel: ${ADMIN_URL}`,
    `👤 Admin ID: ${site.email || site.adminId || site.phone}`,
    password
      ? `🔑 Temporary password: ${password}`
      : `🔑 Password: the one you set. Forgot it? Tap "Forgot password?" on the sign-in page.`,
    ...(site.passwordChanged && !password ? [] : ["", "Please change your password after you sign in, to one only you know."]),
    "",
    "Next step: please add your products from the admin panel, or send us your product photos, names and prices here and we will add them for you.",
    "",
    "Need any help? Just reply to this message. 🙏",
  ].join("\n");
}

const customText = (site) => `${greeting(site)}\n\n`;

export default function WhatsAppMessageModal({ site, kind, notify, onClose, onWelcomeSent }) {
  const isWelcome = kind === "welcome";
  const [text, setText] = useState(() => (isWelcome ? welcomeText(site, "") : customText(site)));
  const [busy, setBusy] = useState(false);
  const [madePassword, setMadePassword] = useState(false);
  const number = whatsappNumber(site.phone);

  const newPassword = async ({ ask = true } = {}) => {
    if (ask && !window.confirm(`Make a new temporary password for ${site.owner || site.name}? Their current password stops working.`)) return;
    setBusy(true);
    try {
      const { data } = await resetOwnerPassword(site.ownerId);
      setText(welcomeText({ ...site, passwordChanged: false }, data.temporaryPassword));
      setMadePassword(true);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  // The customer has not set their own password yet: give the message a temporary password straight away
  const started = useRef(false);
  useEffect(() => {
    if (!isWelcome || site.passwordChanged || started.current) return;
    started.current = true;
    newPassword({ ask: false });
  }, []); // once, as the message opens

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      notify("Message copied");
    } catch {
      notify("Could not copy. Select the message and copy it.", "error");
    }
  };

  return (
    <Modal eyebrow="WHATSAPP" title={isWelcome ? "Welcome message" : "Custom message"} onClose={onClose}
      description={`To ${site.owner || site.name}${site.phone ? ` · ${site.phone}` : ""}`}>
      {!number && <div className="form-error">This customer has no phone number. Copy the message and send it another way.</div>}
      <label className="admin-field">Message
        <textarea rows={isWelcome ? 16 : 8} value={text} onChange={(e) => setText(e.target.value)} autoFocus={!isWelcome} disabled={busy} />
      </label>
      {isWelcome && busy && <p className="field-help"><Spinner /> Making a temporary password…</p>}
      {isWelcome && !busy && madePassword && (
        <p className="field-help">A new temporary password is in the message. The earlier temporary password no longer works, so send this message.</p>
      )}
      {isWelcome && !busy && !madePassword && (
        <p className="field-help">
          {site.passwordChanged ? "The customer has set their own password, so the message doesn't include one. " : ""}
          To send a temporary password, <button type="button" className="text-btn" onClick={() => newPassword()}>make a new temporary password</button> (their current password stops working).
        </p>
      )}
      <div className="product-modal-actions">
        <button type="button" className="outline-btn" onClick={copy}>📋 Copy message</button>
        {number && (
          <a className="primary-btn whatsapp-send-btn" href={`https://wa.me/${number}?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer">
            Open in WhatsApp
          </a>
        )}
        {isWelcome && !site.welcomeSentAt && (
          <button type="button" className="outline-btn" onClick={() => onWelcomeSent(site, true)}>✓ Mark as sent</button>
        )}
      </div>
    </Modal>
  );
}
